import { searchDiagnosisCodesRouteDiagnosisCodesSearchGet } from '../../../services/api/sdk.gen';
import type { DiagnosisCode } from '../../../types/codingData.types';

const normalizeCode = (code: string) =>
  code.replace(/\./g, '').trim().toUpperCase();

/** Module-level cache so revalidation / remounts do not re-hit search. */
const descriptionCache = new Map<string, string | null>();

/** Test helper — clears the module cache between cases. */
export function clearIcdDescriptionCache(): void {
  descriptionCache.clear();
}

/**
 * Resolve ICD-10 codes to display descriptions via the existing diagnosis
 * search endpoint. Misses fail soft to code-only labels.
 */
export async function lookupIcdDescriptions(
  codes: string[]
): Promise<Record<string, string>> {
  const unique = [...new Set(codes.map(c => c.trim()).filter(Boolean))];
  const out: Record<string, string> = {};
  const missing: string[] = [];

  for (const code of unique) {
    const cached = descriptionCache.get(normalizeCode(code));
    if (cached === undefined) {
      missing.push(code);
    } else if (cached !== null) {
      out[code] = cached;
    }
  }

  await Promise.all(
    missing.map(async code => {
      const cacheKey = normalizeCode(code);
      try {
        const response = await searchDiagnosisCodesRouteDiagnosisCodesSearchGet(
          {
            query: { search_text: code },
          }
        );
        const results = (response.data as DiagnosisCode[] | undefined) ?? [];
        const match = results.find(r => normalizeCode(r.name) === cacheKey);
        if (match?.desc) {
          descriptionCache.set(cacheKey, match.desc);
          out[code] = match.desc;
        } else {
          descriptionCache.set(cacheKey, null);
        }
      } catch (error) {
        console.error('Failed to look up ICD description:', code, error);
        // Do not cache hard failures — allow a later retry.
      }
    })
  );

  return out;
}

export function formatCodeWithDescription(
  code: string,
  descriptions: Record<string, string>
): string {
  const desc = descriptions[code];
  return desc ? `${code} — ${desc}` : code;
}
